/**
 * Конвертация Draft <-> Discord API.
 * draftToPayload - то, что уходит в POST/PATCH /messages, вебхук или ответ на взаимодействие.
 * messageToDraft - парсинг готового сообщения (контекстное меню «Спарсить», импорт JSON).
 */
import { newDraft, uid } from './factory.js';
import {
  ButtonStyle,
  ComponentType,
  MessageFlags,
  type ApiComponent,
  type ApiEmbed,
  type ApiEmoji,
  type ApiMessage,
  type ButtonStyleName,
  type DAction,
  type DActionRow,
  type DButton,
  type DContainer,
  type DEmbed,
  type DSelect,
  type DV2Child,
  type DV2Top,
  type Draft,
} from './types.js';

/* ================= Эмодзи ================= */

const CUSTOM_EMOJI_RE = /^<(a?):([\w~]{1,32}):(\d{15,25})>$/;

export function emojiToApi(value: string): ApiEmoji | undefined {
  const v = (value || '').trim();
  if (!v) return undefined;
  const m = CUSTOM_EMOJI_RE.exec(v);
  if (m) return { id: m[3], name: m[2], animated: m[1] === 'a' };
  return { name: v };
}

export function emojiFromApi(e?: ApiEmoji | null): string {
  if (!e) return '';
  if (e.id) return `<${e.animated ? 'a' : ''}:${e.name || 'emoji'}:${e.id}>`;
  return e.name || '';
}

export function isCustomEmoji(value: string): boolean {
  return CUSTOM_EMOJI_RE.test((value || '').trim());
}

/* ================= custom_id ================= */

export const CID_PREFIX = 'eb';

export function actionToCustomId(action: DAction, suffix: string): string {
  switch (action.type) {
    case 'template':
      return `${CID_PREFIX}:t:${action.templateId}:${suffix}`;
    case 'role':
      return `${CID_PREFIX}:r:${action.mode[0]}:${action.roleId}:${suffix}`;
    case 'raw':
      return action.customId;
    default:
      return `${CID_PREFIX}:n:${suffix}`;
  }
}

const ROLE_MODES: Record<string, 'toggle' | 'add' | 'remove'> = { t: 'toggle', a: 'add', r: 'remove' };

export function customIdToAction(customId: string): DAction {
  const p = customId.split(':');
  if (p[0] === CID_PREFIX) {
    if (p[1] === 't' && p[2]) return { type: 'template', templateId: p[2] };
    if (p[1] === 'r' && p[3] && ROLE_MODES[p[2]]) return { type: 'role', roleId: p[3], mode: ROLE_MODES[p[2]] };
    if (p[1] === 'n') return { type: 'none' };
  }
  return { type: 'raw', customId };
}

export function actionToOptionValue(action: DAction, optionId: string, rawValue?: string): string {
  switch (action.type) {
    case 'template':
      return `t:${action.templateId}:${optionId}`;
    case 'role':
      return `r:${action.mode[0]}:${action.roleId}:${optionId}`;
    case 'raw':
      return rawValue || action.customId || optionId;
    default:
      return `n:${optionId}`;
  }
}

export function optionValueToAction(value: string, ours: boolean): DAction {
  if (!ours) return { type: 'raw', customId: value };
  const p = value.split(':');
  if (p[0] === 't' && p[1]) return { type: 'template', templateId: p[1] };
  if (p[0] === 'r' && p[2] && ROLE_MODES[p[1]]) return { type: 'role', roleId: p[2], mode: ROLE_MODES[p[1]] };
  return { type: 'none' };
}

/* ================= Draft -> API ================= */

export interface PayloadFile {
  /** итоговое имя файла в Discord */
  name: string;
  /** https://..., upload://id/name */
  source: string;
  description?: string;
}

export interface BuildOptions {
  target: 'bot' | 'webhook' | 'interaction';
  /** можно ли пинговать @everyone/@here (проверяется по правам автора) */
  allowEveryone?: boolean;
  /** сборка для PATCH существующего сообщения */
  edit?: { wasV2: boolean };
}

export interface BuildResult {
  body: Record<string, any>;
  files: PayloadFile[];
}

class FileCollector {
  files: PayloadFile[] = [];
  private byOriginal = new Map<string, string>();

  constructor(draft: Draft) {
    for (const a of draft.attachments) {
      const base = sanitizeFileName(a.name || 'file');
      const name = this.unique(a.spoiler && !base.startsWith('SPOILER_') ? `SPOILER_${base}` : base);
      this.byOriginal.set(a.name, name);
      this.files.push({ name, source: a.source, description: a.description || undefined });
    }
  }

  private unique(name: string): string {
    let candidate = name;
    let i = 1;
    while (this.files.some((f) => f.name === candidate)) {
      const dot = name.lastIndexOf('.');
      candidate = dot > 0 ? `${name.slice(0, dot)}_${i}${name.slice(dot)}` : `${name}_${i}`;
      i++;
    }
    return candidate;
  }

  /** Превращает ссылку из черновика в ссылку для API, прикрепляя файлы при необходимости. */
  url(value: string): string {
    const v = (value || '').trim();
    if (!v) return v;
    if (v.startsWith('attachment://')) {
      const original = v.slice('attachment://'.length);
      const mapped = this.byOriginal.get(original);
      return `attachment://${mapped ?? original}`;
    }
    if (v.startsWith('upload://')) {
      const existing = this.byOriginal.get(v);
      if (existing) return `attachment://${existing}`;
      const rawName = v.split('/').pop() || 'image.png';
      const name = this.unique(sanitizeFileName(decodeURIComponent(rawName)));
      this.byOriginal.set(v, name);
      this.files.push({ name, source: v });
      return `attachment://${name}`;
    }
    return v;
  }

  attachmentName(original: string): string {
    return this.byOriginal.get(original) ?? original;
  }
}

export function sanitizeFileName(name: string): string {
  const cleaned = name.replace(/[\\/:*?"<>|\s]+/g, '_').replace(/^\.+/, '');
  return cleaned.slice(0, 100) || 'file';
}

function buttonToApi(b: DButton): ApiComponent {
  const o: ApiComponent = { type: ComponentType.Button, style: ButtonStyle[b.style] ?? 2 };
  if (b.label) o.label = b.label;
  const emoji = emojiToApi(b.emoji);
  if (emoji) o.emoji = emoji;
  if (b.style === 'link') o.url = b.url;
  else o.custom_id = actionToCustomId(b.action, b.id);
  if (b.disabled) o.disabled = true;
  return o;
}

function selectToApi(s: DSelect): ApiComponent {
  const ours = !s.rawCustomId;
  const o: ApiComponent = {
    type: ComponentType.StringSelect,
    custom_id: s.rawCustomId || `${CID_PREFIX}:s:${s.id}`,
    options: s.options.map((opt) => {
      const r: Record<string, any> = {
        label: opt.label,
        value: ours ? actionToOptionValue(opt.action, opt.id) : opt.rawValue || opt.id,
      };
      if (opt.description) r.description = opt.description;
      const emoji = emojiToApi(opt.emoji);
      if (emoji) r.emoji = emoji;
      if (opt.isDefault) r.default = true;
      return r;
    }),
  };
  if (s.placeholder) o.placeholder = s.placeholder;
  o.min_values = s.minValues;
  o.max_values = s.maxValues;
  if (s.disabled) o.disabled = true;
  return o;
}

function rowToApi(row: DActionRow): ApiComponent {
  return {
    type: ComponentType.ActionRow,
    components: row.items.map((it) => (it.type === 'button' ? buttonToApi(it) : selectToApi(it))),
  };
}

function embedToApi(e: DEmbed, fc: FileCollector): ApiEmbed {
  const o: ApiEmbed = {};
  if (e.title) o.title = e.title;
  if (e.url) o.url = e.url;
  if (e.description) o.description = e.description;
  if (e.color !== null && e.color !== undefined) o.color = e.color;
  if (e.timestamp) o.timestamp = new Date(e.timestamp).toISOString();
  if (e.author.name) {
    o.author = { name: e.author.name };
    if (e.author.url) o.author.url = e.author.url;
    if (e.author.iconUrl) o.author.icon_url = fc.url(e.author.iconUrl);
  }
  if (e.footer.text) {
    o.footer = { text: e.footer.text };
    if (e.footer.iconUrl) o.footer.icon_url = fc.url(e.footer.iconUrl);
  }
  if (e.image) o.image = { url: fc.url(e.image) };
  if (e.thumbnail) o.thumbnail = { url: fc.url(e.thumbnail) };
  if (e.fields.length) o.fields = e.fields.map((f) => ({ name: f.name, value: f.value, inline: f.inline }));
  return o;
}

function v2ChildToApi(c: DV2Child, fc: FileCollector): ApiComponent {
  switch (c.type) {
    case 'text':
      return { type: ComponentType.TextDisplay, content: c.content };
    case 'section': {
      const acc = c.accessory;
      return {
        type: ComponentType.Section,
        components: c.texts.map((t) => ({ type: ComponentType.TextDisplay, content: t.content })),
        accessory:
          acc.type === 'button'
            ? buttonToApi(acc)
            : {
                type: ComponentType.Thumbnail,
                media: { url: fc.url(acc.url) },
                ...(acc.description ? { description: acc.description } : {}),
                ...(acc.spoiler ? { spoiler: true } : {}),
              },
      };
    }
    case 'gallery':
      return {
        type: ComponentType.MediaGallery,
        items: c.items.map((it) => ({
          media: { url: fc.url(it.url) },
          ...(it.description ? { description: it.description } : {}),
          ...(it.spoiler ? { spoiler: true } : {}),
        })),
      };
    case 'file':
      return {
        type: ComponentType.File,
        file: { url: `attachment://${fc.attachmentName(c.attachment)}` },
        ...(c.spoiler ? { spoiler: true } : {}),
      };
    case 'separator':
      return { type: ComponentType.Separator, divider: c.divider, spacing: c.spacing };
    case 'row':
      return rowToApi(c);
  }
}

function v2TopToApi(c: DV2Top, fc: FileCollector): ApiComponent {
  if (c.type === 'container') {
    const o: ApiComponent = {
      type: ComponentType.Container,
      components: c.children.map((ch) => v2ChildToApi(ch, fc)),
    };
    if (c.accent !== null && c.accent !== undefined) o.accent_color = c.accent;
    if (c.spoiler) o.spoiler = true;
    return o;
  }
  return v2ChildToApi(c, fc);
}

export function allowedMentions(draft: Draft, allowEveryone: boolean) {
  // Components V2: упоминания <@id>, <@&id>, @everyone остаются кликабельными ссылками,
  // но никого не пингуют. Настройки "Разрешённые упоминания" действуют только для обычных сообщений.
  if (draft.mode === 'v2') return { parse: [] as string[] };
  const parse: string[] = [];
  if (draft.options.mentions.users) parse.push('users');
  if (draft.options.mentions.roles) parse.push('roles');
  if (draft.options.mentions.everyone && allowEveryone) parse.push('everyone');
  return { parse };
}

export function draftToPayload(draft: Draft, opts: BuildOptions): BuildResult {
  const fc = new FileCollector(draft);
  const body: Record<string, any> = {};
  let flags = 0;

  if (draft.mode === 'v2') {
    body.components = draft.v2.map((c) => v2TopToApi(c, fc));
    flags |= MessageFlags.IsComponentsV2;
    if (opts.edit && !opts.edit.wasV2) {
      // классическое -> V2: Discord требует очистить content и embeds
      body.content = '';
      body.embeds = [];
    }
  } else {
    if (draft.content || opts.edit) body.content = draft.content;
    if (draft.embeds.length || opts.edit) body.embeds = draft.embeds.map((e) => embedToApi(e, fc));
    if (draft.rows.length || opts.edit) body.components = draft.rows.map(rowToApi);
    if (draft.poll && !opts.edit) {
      body.poll = {
        question: { text: draft.poll.question },
        answers: draft.poll.answers.map((a) => {
          const media: Record<string, any> = { text: a.text };
          const emoji = emojiToApi(a.emoji);
          if (emoji) media.emoji = emoji;
          return { poll_media: media };
        }),
        duration: draft.poll.durationHours,
        allow_multiselect: draft.poll.multiselect,
      };
    }
  }

  if (draft.options.suppressEmbeds) flags |= MessageFlags.SuppressEmbeds;
  if (draft.options.silent && !opts.edit) flags |= MessageFlags.SuppressNotifications;
  if (flags || opts.edit) body.flags = flags;

  body.allowed_mentions = allowedMentions(draft, !!opts.allowEveryone);

  if (opts.target === 'webhook') {
    if (draft.profile.username.trim()) body.username = draft.profile.username.trim();
    if (draft.profile.avatarUrl.trim()) body.avatar_url = draft.profile.avatarUrl.trim();
  }

  if (fc.files.length || opts.edit) {
    body.attachments = fc.files.map((f, i) => ({
      id: i,
      filename: f.name,
      ...(f.description ? { description: f.description } : {}),
    }));
  }

  return { body, files: fc.files };
}

/* ================= API -> Draft ================= */

export interface ParseResult {
  draft: Draft;
  warnings: string[];
}

const STYLE_NAMES: Record<number, ButtonStyleName> = {
  1: 'primary',
  2: 'secondary',
  3: 'success',
  4: 'danger',
  5: 'link',
};

function buttonFromApi(c: ApiComponent, warnings: string[]): DButton | null {
  if (c.style === 6) {
    warnings.push('Premium-кнопка (SKU) пропущена: её нельзя отправить от имени бота без магазина приложения.');
    return null;
  }
  const style = STYLE_NAMES[c.style] ?? 'secondary';
  const action: DAction = style === 'link' ? { type: 'none' } : customIdToAction(String(c.custom_id ?? ''));
  if (action.type === 'raw') warnings.push(`Кнопка «${c.label ?? ''}» использует чужой custom_id - бот не будет на неё отвечать.`);
  return {
    id: uid(),
    type: 'button',
    style,
    label: c.label ?? '',
    emoji: emojiFromApi(c.emoji),
    url: c.url ?? '',
    disabled: !!c.disabled,
    action,
  };
}

function selectFromApi(c: ApiComponent, warnings: string[]): DSelect | null {
  if (c.type !== ComponentType.StringSelect) {
    warnings.push('Меню выбора пользователей/ролей/каналов пропущено: поддерживаются только текстовые меню.');
    return null;
  }
  const ours = String(c.custom_id ?? '').startsWith(`${CID_PREFIX}:s:`);
  if (!ours) warnings.push(`Меню «${c.placeholder ?? ''}» использует чужой custom_id - бот не будет на него отвечать.`);
  return {
    id: uid(),
    type: 'select',
    placeholder: c.placeholder ?? '',
    minValues: c.min_values ?? 1,
    maxValues: c.max_values ?? 1,
    disabled: !!c.disabled,
    rawCustomId: ours ? undefined : String(c.custom_id ?? ''),
    options: (c.options ?? []).map((o: any) => ({
      id: uid(),
      label: o.label ?? '',
      description: o.description ?? '',
      emoji: emojiFromApi(o.emoji),
      isDefault: !!o.default,
      action: optionValueToAction(String(o.value ?? ''), ours),
      rawValue: ours ? undefined : String(o.value ?? ''),
    })),
  };
}

function rowFromApi(c: ApiComponent, warnings: string[]): DActionRow {
  const items: DActionRow['items'] = [];
  for (const ch of c.components ?? []) {
    if (ch.type === ComponentType.Button) {
      const b = buttonFromApi(ch, warnings);
      if (b) items.push(b);
    } else {
      const s = selectFromApi(ch, warnings);
      if (s) items.push(s);
    }
  }
  return { id: uid(), type: 'row', items };
}

function embedFromApi(e: ApiEmbed): DEmbed {
  return {
    id: uid(),
    color: typeof e.color === 'number' ? e.color : null,
    title: e.title ?? '',
    url: e.url ?? '',
    description: e.description ?? '',
    author: { name: e.author?.name ?? '', url: e.author?.url ?? '', iconUrl: e.author?.icon_url ?? '' },
    footer: { text: e.footer?.text ?? '', iconUrl: e.footer?.icon_url ?? '' },
    timestamp: e.timestamp ?? null,
    image: e.image?.url ?? '',
    thumbnail: e.thumbnail?.url ?? '',
    fields: (e.fields ?? []).map((f) => ({ id: uid(), name: f.name, value: f.value, inline: !!f.inline })),
  };
}

function fileNameFromUrl(url: string): string {
  if (url.startsWith('attachment://')) return url.slice('attachment://'.length);
  try {
    return decodeURIComponent(new URL(url).pathname.split('/').pop() || 'file');
  } catch {
    return 'file';
  }
}

function v2ChildFromApi(c: ApiComponent, warnings: string[]): DV2Child | null {
  switch (c.type) {
    case ComponentType.TextDisplay:
      return { id: uid(), type: 'text', content: c.content ?? '' };
    case ComponentType.Section: {
      const texts = (c.components ?? [])
        .filter((t: ApiComponent) => t.type === ComponentType.TextDisplay)
        .map((t: ApiComponent) => ({ id: uid(), type: 'text' as const, content: t.content ?? '' }));
      const acc = c.accessory as ApiComponent | undefined;
      let accessory: DButton | { type: 'thumbnail'; url: string; description: string; spoiler: boolean } = {
        type: 'thumbnail',
        url: '',
        description: '',
        spoiler: false,
      };
      if (acc?.type === ComponentType.Button) {
        const b = buttonFromApi(acc, warnings);
        if (b) accessory = b;
      } else if (acc?.type === ComponentType.Thumbnail) {
        accessory = {
          type: 'thumbnail',
          url: acc.media?.url ?? '',
          description: acc.description ?? '',
          spoiler: !!acc.spoiler,
        };
      }
      return { id: uid(), type: 'section', texts, accessory };
    }
    case ComponentType.MediaGallery:
      return {
        id: uid(),
        type: 'gallery',
        items: (c.items ?? []).map((it: any) => ({
          id: uid(),
          url: it.media?.url ?? '',
          description: it.description ?? '',
          spoiler: !!it.spoiler,
        })),
      };
    case ComponentType.File:
      return { id: uid(), type: 'file', attachment: fileNameFromUrl(c.file?.url ?? ''), spoiler: !!c.spoiler };
    case ComponentType.Separator:
      return { id: uid(), type: 'separator', divider: c.divider !== false, spacing: c.spacing === 2 ? 2 : 1 };
    case ComponentType.ActionRow:
      return rowFromApi(c, warnings);
    default:
      warnings.push(`Неизвестный компонент type=${c.type} пропущен.`);
      return null;
  }
}

function v2TopFromApi(c: ApiComponent, warnings: string[]): DV2Top | null {
  if (c.type === ComponentType.Container) {
    const container: DContainer = {
      id: uid(),
      type: 'container',
      accent: typeof c.accent_color === 'number' ? c.accent_color : null,
      spoiler: !!c.spoiler,
      children: [],
    };
    for (const ch of c.components ?? []) {
      const parsed = v2ChildFromApi(ch, warnings);
      if (parsed) container.children.push(parsed);
    }
    return container;
  }
  return v2ChildFromApi(c, warnings);
}

/** Сообщение (из API или JSON payload) -> черновик. */
export function messageToDraft(msg: Partial<ApiMessage> & Record<string, any>): ParseResult {
  const warnings: string[] = [];
  const draft = newDraft();
  const flags = Number(msg.flags ?? 0);
  draft.mode = flags & MessageFlags.IsComponentsV2 ? 'v2' : 'classic';
  draft.options.silent = !!(flags & MessageFlags.SuppressNotifications);
  draft.options.suppressEmbeds = !!(flags & MessageFlags.SuppressEmbeds);

  // вложения: в API у сообщения - attachments с url, в payload - только filename
  for (const a of msg.attachments ?? []) {
    const anyA = a as any;
    if (!anyA.url) continue;
    const name: string = anyA.filename ?? fileNameFromUrl(anyA.url);
    const spoiler = name.startsWith('SPOILER_');
    draft.attachments.push({
      id: uid(),
      name: spoiler ? name.slice('SPOILER_'.length) : name,
      source: anyA.url,
      spoiler,
      description: anyA.description ?? '',
    });
  }

  if (draft.mode === 'v2') {
    for (const c of msg.components ?? []) {
      const parsed = v2TopFromApi(c, warnings);
      if (parsed) draft.v2.push(parsed);
    }
    // ссылки на attachment://SPOILER_x -> имена без префикса
    const fixName = (n: string) => (n.startsWith('SPOILER_') ? n.slice(8) : n);
    const walk = (c: DV2Top) => {
      if (c.type === 'file') c.attachment = fixName(c.attachment);
      if (c.type === 'container') c.children.forEach(walk);
    };
    draft.v2.forEach(walk);
  } else {
    draft.content = msg.content ?? '';
    const embeds = (msg.embeds ?? []).filter((e: ApiEmbed) => !e.type || e.type === 'rich');
    if ((msg.embeds ?? []).length > embeds.length) {
      warnings.push('Автоматические превью ссылок (не rich-эмбеды) пропущены - Discord создаёт их сам.');
    }
    draft.embeds = embeds.map(embedFromApi);
    draft.rows = (msg.components ?? [])
      .filter((c: ApiComponent) => c.type === ComponentType.ActionRow)
      .map((c: ApiComponent) => rowFromApi(c, warnings))
      .filter((r: DActionRow) => r.items.length > 0);

    if (msg.poll) {
      const hours =
        msg.poll.duration ??
        (msg.poll.expiry && msg.timestamp
          ? Math.max(1, Math.round((Date.parse(msg.poll.expiry) - Date.parse(msg.timestamp)) / 3_600_000))
          : 24);
      draft.poll = {
        question: msg.poll.question?.text ?? '',
        answers: msg.poll.answers.map((a) => ({
          id: uid(),
          text: a.poll_media?.text ?? '',
          emoji: emojiFromApi(a.poll_media?.emoji),
        })),
        durationHours: Math.min(768, Math.max(1, hours)),
        multiselect: !!msg.poll.allow_multiselect,
      };
    }
  }

  if (msg.webhook_id && msg.author) {
    draft.profile.username = msg.author.username ?? msg.username ?? '';
    if (msg.author.avatar) {
      draft.profile.avatarUrl = `https://cdn.discordapp.com/avatars/${msg.author.id}/${msg.author.avatar}.png?size=256`;
    }
  } else if (typeof msg.username === 'string') {
    draft.profile.username = msg.username;
    if (typeof msg.avatar_url === 'string') draft.profile.avatarUrl = msg.avatar_url;
  }

  const am = msg.allowed_mentions as { parse?: string[] } | undefined;
  if (am?.parse) {
    draft.options.mentions = {
      everyone: am.parse.includes('everyone'),
      roles: am.parse.includes('roles'),
      users: am.parse.includes('users'),
    };
  }

  return { draft, warnings };
}

/**
 * Импорт произвольного JSON: наш Draft, payload Discord API,
 * готовое сообщение или бэкап Discohook ({ messages: [{ data }] }).
 */
export function importAnyJson(json: any): ParseResult {
  if (!json || typeof json !== 'object') throw new Error('Ожидался JSON-объект');
  if (json.version === 1 && (json.mode === 'classic' || json.mode === 'v2') && Array.isArray(json.embeds)) {
    return { draft: json as Draft, warnings: [] };
  }
  if (Array.isArray(json.messages) && json.messages[0]?.data) {
    const res = messageToDraft(json.messages[0].data);
    if (json.messages.length > 1) res.warnings.push(`В бэкапе ${json.messages.length} сообщений - импортировано первое.`);
    return res;
  }
  if ('content' in json || 'embeds' in json || 'components' in json || 'poll' in json) {
    return messageToDraft(json);
  }
  throw new Error('Не удалось распознать формат JSON');
}
